import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, Search, GraduationCap, Building2, PlusCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, V1 } from "@/lib/api";

export default function Schools() {
  const [sp, setSp] = useSearchParams();
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ provinces: [], districts: [], levels: [] });
  const [loading, setLoading] = useState(true);

  const q = sp.get("q") || "";
  const province = sp.get("il") || "";
  const level = sp.get("seviye") || "";

  useEffect(() => {
    api.get(`${V1}/schools/facets`).then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (province) params.set("province", province);
    if (level) params.set("level", level);
    api.get(`${V1}/schools?${params.toString()}&limit=60`).then((r) => {
      setItems(r.data.items || []);
    }).finally(() => setLoading(false));
  }, [q, province, level]);

  const update = (k, v) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v); else next.delete(k);
    setSp(next, { replace: true });
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12" data-testid="schools-page">
      <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-accent mb-2">Okul Rehberi</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">Özel Okulları Keşfet</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">İl, ilçe ve eğitim seviyesine göre filtreleyerek bursluluk sınavı açan okulları bulun.</p>
        </div>
        <Button asChild size="lg" variant="outline" className="border-accent/40 hover:bg-accent/5" data-testid="schools-add-cta">
          <Link to="/okul-kayit"><PlusCircle className="h-4 w-4" /> Okulunuzu Ekleyin</Link>
        </Button>
      </div>

      <div className="grid md:grid-cols-4 gap-3 mb-8 p-4 rounded-xl border border-border bg-card">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Okul ara..."
            value={q}
            onChange={(e) => update("q", e.target.value)}
            className="pl-9"
            data-testid="schools-search-input"
          />
        </div>
        <Select value={province || "all"} onValueChange={(v) => update("il", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="schools-province-select"><SelectValue placeholder="Tüm İller" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm İller</SelectItem>
            {facets.provinces.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={level || "all"} onValueChange={(v) => update("seviye", v === "all" ? "" : v)}>
          <SelectTrigger data-testid="schools-level-select"><SelectValue placeholder="Tüm Seviyeler" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Seviyeler</SelectItem>
            {facets.levels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-center py-16">Yükleniyor…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-24">
          <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
          <div className="font-display text-xl">Filtrelere uyan okul bulunamadı.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="schools-grid">
          {items.map((s) => (
            <Link
              key={s.id}
              to={`/okul/${s.slug}`}
              data-testid={`school-card-${s.slug}`}
              className="group overflow-hidden rounded-xl border border-border bg-card hover:border-primary/40"
              style={{ transition: "transform 0.2s ease, border-color 0.2s ease" }}
            >
              <div className="aspect-[16/10] overflow-hidden bg-muted relative">
                <img src={s.cover_url} alt={s.name} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" />
                {s.featured && (
                  <span className="absolute top-3 left-3 bg-accent text-accent-foreground text-[10px] uppercase tracking-widest px-2 py-1 rounded">Öne Çıkan</span>
                )}
              </div>
              <div className="p-5">
                <div className="text-xs text-muted-foreground flex items-center gap-1 mb-2">
                  <MapPin className="h-3 w-3" /> {s.province} / {s.district}
                </div>
                <div className="font-display font-bold text-lg leading-tight mb-2">{s.name}</div>
                <div className="text-sm text-muted-foreground line-clamp-2">{s.short_intro}</div>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <div className="flex flex-wrap gap-1">
                    {(s.education_levels || []).slice(0, 2).map((l) => (
                      <span key={l} className="px-2 py-0.5 bg-muted rounded text-[10px] uppercase tracking-widest">{l}</span>
                    ))}
                  </div>
                  {s.tuition_min && <span className="text-muted-foreground">{s.tuition_min.toLocaleString("tr-TR")} ₺+</span>}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
